import React, { useState } from 'react';
import { Container, Typography, Stack, Box, Paper, Chip, Grid, Card, CardContent, Divider } from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Http as HttpIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Mic as MicIcon,
  Settings as SettingsIcon,
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';

const FlowStep = ({ step, position, onHover, isActive, onClick }) => {
  const getStepColor = (type) => {
    switch (type) {
      case 'client': return '#2563eb';
      case 'server': return '#10b981';
      case 'action': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'POST': return '#ef4444';
      case 'GET': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 280,
      }}
      onMouseEnter={() => onHover && onHover(step)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      <Paper
        elevation={isActive ? 8 : 2}
        onClick={() => onClick && onClick(step)}
        sx={{
          p: 2,
          width: '100%',
          backgroundColor: getStepColor(step.type),
          color: 'white',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          transform: isActive ? 'scale(1.05)' : 'scale(1)',
          cursor: 'pointer',
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {step.icon}
            <Typography variant="subtitle2" fontWeight={600}>
              {step.title}
            </Typography>
          </Stack>
          {step.endpoint && (
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: 1,
                p: 1,
                mt: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={step.method}
                  size="small"
                  sx={{
                    backgroundColor: getMethodColor(step.method),
                    color: 'white',
                    fontWeight: 600,
                    height: 20,
                    fontSize: '0.7rem',
                  }}
                />
                <Typography variant="caption" sx={{ fontSize: '0.7rem', wordBreak: 'break-word' }}>
                  {step.endpoint}
                </Typography>
              </Stack>
            </Box>
          )}
          {step.description && (
            <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
              {step.description}
            </Typography>
          )}
        </Stack>
      </Paper>
      {step.stepNumber && (
        <Chip
          label={`Step ${step.stepNumber}`}
          size="small"
          sx={{
            mt: 1,
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 600,
          }}
        />
      )}
    </Box>
  );
};

const Arrow = ({ direction = 'down', length = 60, label, isPolling = false }) => {
  const arrowStyle = {
    display: 'flex',
    flexDirection: direction === 'down' ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: length,
    minWidth: direction === 'down' ? 0 : length,
  };

  return (
    <Box sx={arrowStyle}>
      {direction === 'down' ? (
        <ArrowDownwardIcon sx={{ color: isPolling ? '#f59e0b' : 'primary.main', fontSize: 40 }} />
      ) : (
        <ArrowForwardIcon sx={{ color: isPolling ? '#f59e0b' : 'primary.main', fontSize: 40 }} />
      )}
      {isPolling && (
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
          }}
        >
          <RefreshIcon sx={{ fontSize: 16, color: '#f59e0b', animation: 'spin 2s linear infinite' }} />
          <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
            Polling
          </Typography>
        </Box>
      )}
      {label && (
        <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 600 }}>
          {label}
        </Typography>
      )}
    </Box>
  );
};

const flowSteps = [
  {
    stepNumber: 1,
    type: 'client',
    title: 'Start Voice Session',
    description: 'Client initiates voice chat',
    icon: <PlayArrowIcon />,
    method: 'POST',
    endpoint: '/conversations/{conversation_id}/voice/start',
    position: { x: 0, y: 0 },
  },
  {
    stepNumber: 2,
    type: 'server',
    title: 'Create Session & Peer Connection',
    description: 'Server generates session ID and creates RTCPeerConnection',
    icon: <SettingsIcon />,
    position: { x: 0, y: 1 },
  },
  {
    stepNumber: 3,
    type: 'client',
    title: 'Initialize RTCPeerConnection',
    description: 'Client creates peer connection with ICE servers',
    icon: <SettingsIcon />,
    position: { x: 0, y: 2 },
  },
  {
    stepNumber: 4,
    type: 'client',
    title: 'Get User Media',
    description: 'Request microphone access',
    icon: <MicIcon />,
    position: { x: 0, y: 3 },
  },
  {
    stepNumber: 5,
    type: 'client',
    title: 'Handle ICE Candidates',
    description: 'Send ICE candidates as they are generated',
    icon: <SendIcon />,
    method: 'POST',
    endpoint: '/signal/{voice_session_id}/ice',
    position: { x: 0, y: 4 },
  },
  {
    stepNumber: 6,
    type: 'client',
    title: 'Create & Send Offer',
    description: 'Generate WebRTC offer SDP',
    icon: <SendIcon />,
    method: 'POST',
    endpoint: '/signal/{voice_session_id}/offer',
    position: { x: 0, y: 5 },
  },
  {
    stepNumber: 7,
    type: 'server',
    title: 'Process Offer & Create Answer',
    description: 'Server processes offer and generates answer SDP',
    icon: <SettingsIcon />,
    position: { x: 1, y: 5 },
  },
  {
    stepNumber: 8,
    type: 'client',
    title: 'Poll for Answer',
    description: 'Client polls server for answer SDP',
    icon: <RefreshIcon />,
    method: 'GET',
    endpoint: '/signal/{voice_session_id}/answer',
    isPolling: true,
    position: { x: 0, y: 6 },
  },
  {
    stepNumber: 9,
    type: 'server',
    title: 'Return Answer',
    description: 'Server returns answer when ready',
    icon: <CheckCircleIcon />,
    position: { x: 1, y: 6 },
  },
  {
    stepNumber: 10,
    type: 'client',
    title: 'Set Remote Description',
    description: 'Client sets answer as remote description',
    icon: <CheckCircleIcon />,
    position: { x: 0, y: 7 },
  },
  {
    stepNumber: 11,
    type: 'action',
    title: 'Voice Communication Active',
    description: 'Audio streams active in both directions',
    icon: <VolumeUpIcon />,
    position: { x: 0, y: 8 },
  },
];

export default function RestVoiceChatFlow() {
  const [hoveredStep, setHoveredStep] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);

  const handleStepClick = (step) => {
    setSelectedStep(step);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Typography variant="h4" textAlign="center">
          REST-Based Voice Chat Flow Diagram
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Visual representation of the REST API flow for WebRTC voice chat using polling instead of WebSocket
        </Typography>
      </Stack>

      {/* Legend */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 4,
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Legend</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 24, height: 24, backgroundColor: '#2563eb', borderRadius: 1 }} />
              <Typography variant="body2">Client Action</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 24, height: 24, backgroundColor: '#10b981', borderRadius: 1 }} />
              <Typography variant="body2">Server Action</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 24, height: 24, backgroundColor: '#f59e0b', borderRadius: 1 }} />
              <Typography variant="body2">Active Communication</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="POST" size="small" sx={{ backgroundColor: '#ef4444', color: 'white' }} />
              <Typography variant="body2">POST Request</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="GET" size="small" sx={{ backgroundColor: '#3b82f6', color: 'white' }} />
              <Typography variant="body2">GET Request (Polling)</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Flow Diagram */}
      <Paper
        variant="outlined"
        sx={{
          p: 4,
          mb: 4,
          backgroundColor: '#f8fafc',
          minHeight: 1400,
          position: 'relative',
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Steps 1-5: Vertical Flow */}
          {flowSteps.slice(0, 5).map((step, index) => (
            <React.Fragment key={step.stepNumber}>
              <FlowStep
                step={step}
                position={step.position}
                onHover={setHoveredStep}
                isActive={hoveredStep === step || selectedStep === step}
                onClick={handleStepClick}
              />
              {index < 4 && <Arrow direction="down" length={80} />}
            </React.Fragment>
          ))}

          {/* Step 6: Client sends offer */}
          <FlowStep
            step={flowSteps[5]}
            position={flowSteps[5].position}
            onHover={setHoveredStep}
            isActive={hoveredStep === flowSteps[5] || selectedStep === flowSteps[5]}
            onClick={handleStepClick}
          />
          <Arrow direction="down" length={60} label="POST /signal/{id}/offer" />

          {/* Horizontal flow: Client -> Server -> Client */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              width: '100%',
              justifyContent: 'center',
              my: 2,
            }}
          >
            {/* Client side - Offer sent */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Paper
                sx={{
                  p: 1.5,
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: 1,
                  minWidth: 200,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                  Client: Offer Sent
                </Typography>
              </Paper>
            </Box>

            <Arrow direction="right" length={150} label="Processing" />

            {/* Server side - Process offer */}
            <FlowStep
              step={flowSteps[6]}
              position={flowSteps[6].position}
              onHover={setHoveredStep}
              isActive={hoveredStep === flowSteps[6] || selectedStep === flowSteps[6]}
              onClick={handleStepClick}
            />

            <Arrow direction="left" length={150} label="Answer Ready" />
          </Box>

          {/* Step 8: Polling section with visual loop */}
          <Arrow direction="down" length={60} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              width: '100%',
              maxWidth: 600,
              p: 3,
              backgroundColor: '#fef3c7',
              borderRadius: 2,
              border: '2px dashed #f59e0b',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e', mb: 1 }}>
              Polling Loop (Every 1 second)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <FlowStep
                step={flowSteps[7]}
                position={flowSteps[7].position}
                onHover={setHoveredStep}
                isActive={hoveredStep === flowSteps[7] || selectedStep === flowSteps[7]}
                onClick={handleStepClick}
              />
              <Arrow direction="right" length={100} isPolling={true} />
              <FlowStep
                step={flowSteps[8]}
                position={flowSteps[8].position}
                onHover={setHoveredStep}
                isActive={hoveredStep === flowSteps[8] || selectedStep === flowSteps[8]}
                onClick={handleStepClick}
              />
              <Arrow direction="left" length={100} isPolling={true} label="Loop until ready" />
            </Box>
            <Typography variant="caption" sx={{ color: '#92400e', fontStyle: 'italic' }}>
              Client continuously polls server until answer is ready (ready: true)
            </Typography>
          </Box>

          {/* Steps 9-11: Final steps */}
          <Arrow direction="down" length={60} />
          <FlowStep
            step={flowSteps[9]}
            position={flowSteps[9].position}
            onHover={setHoveredStep}
            isActive={hoveredStep === flowSteps[9] || selectedStep === flowSteps[9]}
            onClick={handleStepClick}
          />
          <Arrow direction="down" length={80} />
          <FlowStep
            step={flowSteps[10]}
            position={flowSteps[10].position}
            onHover={setHoveredStep}
            isActive={hoveredStep === flowSteps[10] || selectedStep === flowSteps[10]}
            onClick={handleStepClick}
          />
        </Box>
      </Paper>

      {/* Detailed Information Panel */}
      {selectedStep && (
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">
                Step {selectedStep.stepNumber}: {selectedStep.title}
              </Typography>
              <Divider />
              <Typography variant="body2" color="text.secondary">
                {selectedStep.description || 'No additional details'}
              </Typography>
              {selectedStep.endpoint && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      REST Endpoint:
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={selectedStep.method}
                        size="small"
                        sx={{
                          backgroundColor: selectedStep.method === 'POST' ? '#ef4444' : '#3b82f6',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {selectedStep.endpoint}
                      </Typography>
                    </Stack>
                  </Box>
                </>
              )}
              {selectedStep.isPolling && (
                <>
                  <Divider />
                  <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#92400e' }}>
                      Polling Mechanism:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#92400e' }}>
                      Client polls the server every 1 second (1000ms) until the answer is ready.
                      This is an alternative to WebSocket push notifications.
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Key Differences from WebSocket */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          backgroundColor: 'info.light',
          color: 'info.contrastText',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          REST vs WebSocket Approach
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              REST Approach (This Flow)
            </Typography>
            <Stack spacing={0.5} sx={{ pl: 2 }}>
              <Typography variant="body2">• Uses HTTP POST/GET requests</Typography>
              <Typography variant="body2">• Client polls server for answer (polling mechanism)</Typography>
              <Typography variant="body2">• Simpler to implement and debug</Typography>
              <Typography variant="body2">• No persistent connection needed</Typography>
              <Typography variant="body2">• Higher latency due to polling interval</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              WebSocket Approach (Previous Flow)
            </Typography>
            <Stack spacing={0.5} sx={{ pl: 2 }}>
              <Typography variant="body2">• Uses persistent WebSocket connection</Typography>
              <Typography variant="body2">• Server pushes answer immediately when ready</Typography>
              <Typography variant="body2">• Lower latency, real-time communication</Typography>
              <Typography variant="body2">• Requires connection management</Typography>
              <Typography variant="body2">• More complex error handling</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Container>
  );
}

